import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { adminService } from '../../../services/adminService';
import { realtimeService } from '../../../services/realtimeService';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [newUser, setNewUser] = useState({
    fullName: '',
    email: '',
    role: 'user'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUsers();

    // Subscribe to real-time user_profiles changes
    const subscription = realtimeService?.subscribeToUserProfiles((payload) => {
      const { eventType, new: newRecord, old: oldRecord } = payload;

      if (eventType === 'INSERT') {
        // Add new user to the list
        const newUser = {
          id: newRecord?.id,
          name: newRecord?.full_name,
          email: newRecord?.email,
          avatar: newRecord?.avatar_url || '/assets/images/no_image.png',
          avatarAlt: `${newRecord?.full_name} profile picture`,
          role: newRecord?.role,
          status: 'active',
          sessionsCompleted: 0,
          lastActive: newRecord?.updated_at,
          verificationStatus: 'verified',
          createdAt: newRecord?.created_at,
          updatedAt: newRecord?.updated_at
        };
        setUsers((prev) => [newUser, ...prev]);
      } else if (eventType === 'UPDATE') {
        // Update existing user
        setUsers((prev) =>
          prev?.map((user) =>
            user?.id === newRecord?.id
              ? {
                  ...user,
                  name: newRecord?.full_name,
                  email: newRecord?.email,
                  avatar: newRecord?.avatar_url || '/assets/images/no_image.png',
                  role: newRecord?.role,
                  lastActive: newRecord?.updated_at,
                  updatedAt: newRecord?.updated_at
                }
              : user
          )
        );
      } else if (eventType === 'DELETE') {
        // Remove deleted user
        setUsers((prev) => prev?.filter((user) => user?.id !== oldRecord?.id));
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await adminService?.getAllUsers();
      
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load users');
        return;
      }
      
      setUsers(data || []);
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e?.preventDefault();
    setSaving(true);
    
    try {
      const { error: createError } = await adminService?.createUser(newUser);
      
      if (createError) {
        alert(`Failed to create user: ${createError?.message}`);
        return;
      }
      
      // Reset form and close modal
      setNewUser({ fullName: '', email: '', role: 'user' });
      setShowAddUserModal(false);
      
      // Reload users
      loadUsers();
    } catch (err) {
      alert(`Failed to create user: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const { error: updateError } = await adminService?.updateUserRole(userId, newRole);
      
      if (updateError) {
        alert(`Failed to update role: ${updateError?.message}`);
        return;
      }
      
      // Reload users
      loadUsers();
    } catch (err) {
      alert(`Failed to update role: ${err?.message}`);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      id: user?.id,
      fullName: user?.name,
      email: user?.email,
      role: user?.role
    });
    setShowEditUserModal(true);
    setOpenDropdownId(null);
  };

  const handleUpdateUser = async (e) => {
    e?.preventDefault();
    setSaving(true);
    
    try {
      const { error: updateError } = await adminService?.updateUser(editingUser?.id, {
        fullName: editingUser?.fullName,
        email: editingUser?.email,
        role: editingUser?.role
      });
      
      if (updateError) {
        alert(`Failed to update user: ${updateError?.message}`);
        return;
      }
      
      // Reset form and close modal
      setEditingUser(null);
      setShowEditUserModal(false);
      
      // Reload users
      loadUsers();
    } catch (err) {
      alert(`Failed to update user: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      const { error: deleteError } = await adminService?.deleteUser(userId);
      
      if (deleteError) {
        alert(`Failed to delete user: ${deleteError?.message}`);
        return;
      }
      
      // Reload users
      loadUsers();
    } catch (err) {
      alert(`Failed to delete user: ${err?.message}`);
    }
    
    setOpenDropdownId(null);
  };

  const handleSuspendUser = async (userId) => {
    try {
      const { error: suspendError } = await adminService?.suspendUser(userId);
      
      if (suspendError) {
        alert(`Failed to suspend user: ${suspendError?.message}`);
        return;
      }
      
      // Reload users
      loadUsers();
    } catch (err) {
      alert(`Failed to suspend user: ${err?.message}`);
    }
    
    setOpenDropdownId(null);
  };

  const toggleDropdown = (userId) => {
    setOpenDropdownId(openDropdownId === userId ? null : userId);
  };

  const roleOptions = [
  { value: "all", label: "All Roles" },
  { value: "admin", label: "Administrator" },
  { value: "helper", label: "Helper" },
  { value: "user", label: "User" }];


  const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "pending", label: "Pending Verification" }];


  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: "bg-error/10 text-error",
      helper: "bg-primary/10 text-primary",
      user: "bg-secondary/10 text-secondary"
    };
    return colors?.[role] || "bg-muted text-muted-foreground";
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Admin",
      helper: "Helper",
      user: "User"
    };
    return labels?.[role] || role;
  };

  const getVerificationIcon = (status) => {
    if (status === "verified") return { name: "CheckCircle2", color: "text-success" };
    if (status === "pending") return { name: "Clock", color: "text-warning" };
    return { name: "XCircle", color: "text-error" };
  };

  const formatLastActive = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const filteredUsers = users?.filter((user) => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-12">
          <p className="text-error mb-4">{error}</p>
          <button 
            onClick={loadUsers}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Icon name="Users" size={24} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold">User Management</h3>
              <p className="text-sm text-muted-foreground">{filteredUsers?.length} users found</p>
            </div>
          </div>
          <Button
            variant="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowAddUserModal(true)}>
            Add User
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Input
            type="search"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)} />

          <Select
            placeholder="Filter by role"
            options={roleOptions}
            value={roleFilter}
            onChange={setRoleFilter} />

          <Select
            placeholder="Filter by status"
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter} />

        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Role</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Sessions</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Active</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Verification</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers?.map((user) => {
                const verificationIcon = getVerificationIcon(user?.verificationStatus);
                return (
                  <tr key={user?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={user?.avatar}
                          alt={user?.avatarAlt}
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => e.target.src = '/assets/images/no_image.png'} />

                        <div>
                          <p className="text-sm font-medium">{user?.name}</p>
                          <p className="text-xs text-muted-foreground">{user?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>
                        {getRoleLabel(user?.role)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center gap-1.5 text-sm ${user?.status === 'active' ? 'text-success' : 'text-muted-foreground'}`}>
                        <span className={`w-2 h-2 rounded-full ${user?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'}`} />
                        {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm font-medium text-data">{user?.sessionsCompleted}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-muted-foreground">{formatLastActive(user?.lastActive)}</span>
                    </td>
                    <td className="py-4 px-4">
                      <Icon name={verificationIcon?.name} size={20} className={verificationIcon?.color} />
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          iconName="Edit" 
                          onClick={() => handleEditUser(user)}
                        />
                        <div className="relative">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            iconName="MoreVertical" 
                            onClick={() => toggleDropdown(user?.id)}
                          />
                          {openDropdownId === user?.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-border z-10">
                              <button
                                onClick={() => handleEditUser(user)}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-muted flex items-center gap-2"
                              >
                                <Icon name="Edit" size={16} />
                                Edit User
                              </button>
                              <button
                                onClick={() => handleSuspendUser(user?.id)}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-muted flex items-center gap-2 text-warning"
                              >
                                <Icon name="Ban" size={16} />
                                Suspend User
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user?.id)}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-muted flex items-center gap-2 text-error"
                              >
                                <Icon name="Trash2" size={16} />
                                Delete User
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>);

              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold">Add New User</h3>
              <button
                onClick={() => setShowAddUserModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter full name"
                  value={newUser?.fullName}
                  onChange={(e) => setNewUser({ ...newUser, fullName: e?.target?.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newUser?.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e?.target?.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <Select
                  placeholder="Select role"
                  options={[
                    { value: 'user', label: 'User' },
                    { value: 'helper', label: 'Helper' },
                    { value: 'admin', label: 'Administrator' }
                  ]}
                  value={newUser?.role}
                  onChange={(value) => setNewUser({ ...newUser, role: value })}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddUserModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? 'Creating...' : 'Create User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold">Edit User</h3>
              <button
                onClick={() => setShowEditUserModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter full name"
                  value={editingUser?.fullName}
                  onChange={(e) => setEditingUser({ ...editingUser, fullName: e?.target?.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={editingUser?.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e?.target?.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <Select
                  placeholder="Select role"
                  options={[
                    { value: 'user', label: 'User' },
                    { value: 'helper', label: 'Helper' },
                    { value: 'admin', label: 'Administrator' }
                  ]}
                  value={editingUser?.role}
                  onChange={(value) => setEditingUser({ ...editingUser, role: value })}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditUserModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? 'Updating...' : 'Update User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;